import { useContext, useEffect, useRef, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { useLocation, useNavigate } from 'react-router-dom';
import { TranslationContext } from '../../../assets/contexts/translationContext';
import { CARD_TYPES } from '../../../assets/constants/cardTypes';
import {
  getCardById,
  refreshCardById,
  refreshCards,
} from '../../../assets/services/cards';
import {
  cancelDepositFlow,
  clearDepositSession,
  getDepositSession,
  resumeDepositFlow,
  subscribeDepositSession,
} from '../../../assets/services/deposit';
import BankCard from '../../BankCard/BankCard';
import CardPage from '../CardPage/CardPage';
import BottomActionButton from '../../shared/BottomActionButton/BottomActionButton';
import './CardIssueStatus.css';

function getIssueCardId(location, session) {
  return location.state?.cardId || session?.cardId || '';
}

function getCardPreviewName(card) {
  return card?.label || card?.name || card?.staticData?.name || card?.static_data?.name || '';
}

function CardIssueStatus({ mode }) {
  const shouldReduceMotion = useReducedMotion();
  const navigate = useNavigate();
  const location = useLocation();
  const { card: cardCopy, common } = useContext(TranslationContext);
  const [session, setSession] = useState(() => getDepositSession());
  const [issueCard, setIssueCard] = useState(() => {
    const currentSession = getDepositSession();
    return getCardById(getIssueCardId(location, currentSession));
  });
  const sessionRef = useRef(session);
  const cardId = getIssueCardId(location, session);
  const isConfirming = mode === 'confirming';

  useEffect(() => {
    sessionRef.current = session;
  }, [session]);

  useEffect(() => {
    if (!cardId) {
      return undefined;
    }

    const cachedCard = getCardById(cardId);
    if (cachedCard) {
      setIssueCard(cachedCard);
    }

    let isMounted = true;

    refreshCardById(cardId)
      .then((nextCard) => {
        if (isMounted) {
          setIssueCard(nextCard);
        }
      })
      .catch(() => {});

    return () => {
      isMounted = false;
    };
  }, [cardId]);

  useEffect(() => {
    if (!isConfirming) {
      return undefined;
    }

    const currentSession = sessionRef.current;

    if (!currentSession || currentSession.status !== 'confirming') {
      if (currentSession?.status === 'success') {
        navigate('/cards/issue/submitted', {
          replace: true,
          state: { cardId },
        });
        return undefined;
      }

      navigate('/home', { replace: true });
      return undefined;
    }

    const unsubscribe = subscribeDepositSession((nextSession) => {
      if (!nextSession) return;

      setSession(nextSession);

      if (nextSession.status === 'success') {
        refreshCardById(nextSession.cardId || cardId).catch(() => {});
        navigate('/cards/issue/submitted', {
          replace: true,
          state: { cardId: nextSession.cardId || cardId },
        });
      }
    });

    if (currentSession.cardId && currentSession.addressId) {
      resumeDepositFlow(currentSession);
    }

    return unsubscribe;
  }, [cardId, isConfirming, navigate]);

  const handleDone = () => {
    cancelDepositFlow();
    clearDepositSession();
    refreshCards({ silent: true }).catch(() => {});
    navigate('/home', { replace: true });
  };

  const title = isConfirming
    ? cardCopy.issueConfirmingTitle
    : cardCopy.issueSubmittedTitle;
  const subtitle = isConfirming
    ? cardCopy.issueConfirmingText
    : cardCopy.issueSubmittedText;

  return (
    <CardPage className="card-issue-status">
      <div className="card-issue-status__content">
        <motion.div
          className="card-issue-status__hero"
          initial={shouldReduceMotion
            ? { opacity: 0, transform: 'translate3d(0, 0px, 0)' }
            : { opacity: 0, transform: 'translate3d(0, 18px, 0)' }}
          animate={{ opacity: 1, transform: 'translate3d(0, 0px, 0)' }}
          transition={{ duration: 0.34, ease: [0.22, 1, 0.36, 1] }}
        >
          <motion.div
            className="card-issue-status__preview-wrap"
            animate={shouldReduceMotion
              ? { transform: 'translate3d(0, 0px, 0)' }
              : {
                transform: [
                  'translate3d(0, 0px, 0)',
                  'translate3d(0, -5px, 0)',
                  'translate3d(0, 0px, 0)',
                ],
              }}
            transition={shouldReduceMotion
              ? { duration: 0 }
              : { duration: 3.6, repeat: Infinity, ease: 'easeInOut' }}
          >
            <BankCard
              cardStyle={issueCard?.style || 'sunset'}
              cardType={issueCard?.type || CARD_TYPES.VIRTUAL}
              showCardType
              size="detail"
              label={getCardPreviewName(issueCard)}
              detailsText={cardCopy.activatingIssue}
              activating
              className="card-issue-status__preview"
            />
          </motion.div>

          <h1 className="card-issue-status__title">{title}</h1>
          <p className="card-issue-status__subtitle">{subtitle}</p>
        </motion.div>
      </div>

      <BottomActionButton className="card-issue-status__done" onClick={handleDone}>
        {common.done}
      </BottomActionButton>
    </CardPage>
  );
}

export default CardIssueStatus;
